import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
} from '@/components/ui/alert-dialog'
import { Button, ButtonText } from '@/components/ui/button'
import { Heading } from '@/components/ui/heading'
import { CloseIcon, Icon } from '@/components/ui/icon'
import { Input, InputField } from '@/components/ui/input'
import { Text } from '@/components/ui/text'
import React, { useEffect, useState } from 'react'
import { View } from 'react-native'
import type { AlertButton, AlertOptions, AlertStatic, AlertType } from './Alert.types'

interface AlertConfig {
  type: 'alert' | 'prompt'
  title: string
  message?: string
  buttons?: AlertButton[]
  options?: AlertOptions
  promptType?: AlertType
  defaultValue?: string
  onDismiss?: () => void
  id: string
}

type AlertListener = (config: AlertConfig) => void

const alertEmitter = {
  listeners: new Set<AlertListener>(),
  alertIdCounter: 0,
  emit(config: Omit<AlertConfig, 'id'>) {
    const configWithId = { ...config, id: `alert-${++this.alertIdCounter}` }
    this.listeners.forEach(listener => listener(configWithId))
  },
  subscribe(listener: AlertListener) {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  },
}

export const AlertContainer: React.FC = () => {
  const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const currentAlertIdRef = React.useRef<string | null>(null)

  useEffect(() => {
    const unsubscribe = alertEmitter.subscribe(config => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
        closeTimeoutRef.current = null
      }

      if (currentAlertIdRef.current && isOpen) {
        setIsOpen(false)
        setTimeout(() => {
          currentAlertIdRef.current = config.id
          setAlertConfig(config)
          setInputValue(config.defaultValue || '')
          setIsOpen(true)
        }, 500)
      } else {
        currentAlertIdRef.current = config.id
        setAlertConfig(config)
        setInputValue(config.defaultValue || '')
        setIsOpen(true)
      }
    })
    return () => {
      unsubscribe()
    }
  }, [isOpen])

  const handleClose = (buttonIndex?: number) => {
    const closingAlertId = currentAlertIdRef.current
    setIsOpen(false)

    if (alertConfig) {
      const buttons = alertConfig.buttons || []

      if (buttonIndex !== undefined && buttons[buttonIndex]) {
        const button = buttons[buttonIndex]
        if (alertConfig.type === 'prompt' && button.onPress) {
          button.onPress(inputValue)
        } else if (button.onPress) {
          button.onPress()
        }
      }

      if (alertConfig.options?.onDismiss) {
        alertConfig.options.onDismiss()
      }
    }

    closeTimeoutRef.current = setTimeout(() => {
      if (currentAlertIdRef.current === closingAlertId) {
        setAlertConfig(null)
        currentAlertIdRef.current = null
      }
      closeTimeoutRef.current = null
    }, 300)
  }

  if (!alertConfig) return null

  const buttons = alertConfig.buttons || [{ text: 'OK', style: 'default' }]

  const cancelButton = buttons.find(b => b.style === 'cancel')
  const cancelButtonIndex = cancelButton ? buttons.indexOf(cancelButton) : -1
  const actionButtons = buttons.filter(b => b.style !== 'cancel').reverse()

  return (
    <AlertDialog isOpen={isOpen} onClose={() => {}}>
      <AlertDialogBackdrop />
      <AlertDialogContent>
        {cancelButton && (
          <AlertDialogCloseButton
            className="absolute top-3 right-3 z-10"
            onPress={() => handleClose(cancelButtonIndex)}
          >
            <Icon as={CloseIcon} size="sm" className="stroke-background-400" />
          </AlertDialogCloseButton>
        )}
        <AlertDialogHeader>
          <Heading size="md">{alertConfig.title}</Heading>
        </AlertDialogHeader>
        <AlertDialogBody>
          {alertConfig.message && <Text size="sm">{alertConfig.message}</Text>}
          {alertConfig.type === 'prompt' && (
            <View className="mt-4">
              <Input>
                <InputField
                  key={alertConfig.id}
                  value={inputValue}
                  onChangeText={setInputValue}
                  placeholder=""
                  secureTextEntry={alertConfig.promptType === 'secure-text'}
                  inputMode={alertConfig.promptType === 'secure-text' ? undefined : 'text'}
                  autoComplete={alertConfig.promptType === 'secure-text' ? 'password' : 'off'}
                  autoFocus
                />
              </Input>
            </View>
          )}
        </AlertDialogBody>
        <AlertDialogFooter className="mt-4 flex-wrap">
          {actionButtons.map((button, index) => {
            const originalIndex = buttons.indexOf(button)
            const isDestructive = button.style === 'destructive'
            const isPreferred = button.isPreferred

            return (
              <Button
                key={index}
                variant="solid"
                action={isDestructive ? 'negative' : isPreferred ? 'primary' : 'secondary'}
                onPress={() => handleClose(originalIndex)}
              >
                <ButtonText>{button.text || 'OK'}</ButtonText>
              </Button>
            )
          })}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

const Alert: AlertStatic = {
  alert(title: string, message?: string, buttons?: AlertButton[], options?: AlertOptions): void {
    alertEmitter.emit({
      type: 'alert',
      title,
      message,
      buttons,
      options,
    })
  },

  prompt(
    title: string,
    message?: string,
    callbackOrButtons?: ((text: string) => void) | AlertButton[],
    type?: AlertType,
    defaultValue?: string
  ): void {
    let buttons: AlertButton[]

    if (typeof callbackOrButtons === 'function') {
      buttons = [
        { text: 'Cancel', style: 'cancel' },
        { text: 'OK', onPress: (value?: string) => callbackOrButtons(value || '') },
      ]
    } else {
      buttons = callbackOrButtons || [{ text: 'Cancel', style: 'cancel' }, { text: 'OK' }]
    }

    alertEmitter.emit({
      type: 'prompt',
      title,
      message,
      buttons,
      promptType: type,
      defaultValue,
    })
  },
}

export default Alert
